// Contact bookkeeping detail (Phase 7: the second half of the
// `/contacts-list` + `/contacts` merge). Two tabs only — Bookkeeping and
// Statement. People, activity history, and files do NOT render here: they
// render as deep-link cards out (CRM for people/activity, the shared
// document store for files), per the purely-accounting constraint.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { DeepLinkCard } from "@/components/DeepLinkCard";
import { CardField } from "@/components/RecordCard";
import {
  PaymentTermPicker,
  usePaymentTerms,
} from "@/features/payment-terms/PaymentTermPicker";
import {
  ApiRequestError,
  type Bill,
  type ContactFinanceProfile,
  downloadSupplierStatementPdf,
  type Invoice,
  listBills,
  listContacts,
  listInvoices,
  readContactFinanceProfile,
  upsertContactFinanceProfile,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

const TABS = [
  {
    value: "bookkeeping",
    label: "Bookkeeping",
    hint: "Terms and open documents",
  },
  {
    value: "statement",
    label: "Statement",
    hint: "Chronological money in and out",
  },
] as const;

const CONTACT_TYPES = ["customer", "vendor"] as const;

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function isNotFound(cause: unknown): boolean {
  if (cause instanceof ApiRequestError) return cause.status === 404;
  return cause instanceof Error && cause.message.includes("404");
}

const OPEN_STATUSES = new Set([
  "sent",
  "overdue",
  "received",
  "draft",
  "pending",
  "partial",
  "partially_paid",
]);

function isOpen(status: string): boolean {
  return OPEN_STATUSES.has(status.toLowerCase());
}

export function ContactDetailPage() {
  const { contactId } = useParams({ strict: false }) as {
    contactId: string;
  };
  const { scope } = useOrganization();
  const [tab, setTab] = useState<string>("bookkeeping");

  const [profile, setProfile] = useState<ContactFinanceProfile | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [directoryName, setDirectoryName] = useState<string | null>(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docsReloadToken, setDocsReloadToken] = useState(0);
  // The finance profile is keyed by the counterparty legal entity.
  // This page is routed by CRM contact id, so the entity is discovered from
  // the contact's own documents; a contact whose documents carry no entity
  // yet has no finance profile to read or write.
  const [counterpartyLegalEntityId, setCounterpartyLegalEntityId] = useState<
    string | null
  >(null);
  const [docsResolved, setDocsResolved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [contactType, setContactType] = useState<string>(CONTACT_TYPES[0]);
  const [paymentTermId, setPaymentTermId] = useState("none");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [taxNumber, setTaxNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const { terms: paymentTerms, error: paymentTermsError } = usePaymentTerms();

  const [pdfBusy, setPdfBusy] = useState(false);

  const reloadProfile = useCallback(async () => {
    if (!docsResolved) return;
    setProfileLoading(true);
    setProfileError(null);
    if (!counterpartyLegalEntityId) {
      setProfile(null);
      setProfileMissing(false);
      setProfileLoading(false);
      return;
    }
    try {
      setProfile(await readContactFinanceProfile(counterpartyLegalEntityId));
      setProfileMissing(false);
    } catch (cause) {
      if (isNotFound(cause)) {
        setProfile(null);
        setProfileMissing(true);
      } else {
        setProfile(null);
        setProfileMissing(false);
        setProfileError(toErrorMessage(cause));
      }
    } finally {
      setProfileLoading(false);
    }
  }, [counterpartyLegalEntityId, docsResolved, docsError]);

  useEffect(() => {
    void reloadProfile();
  }, [reloadProfile]);

  useEffect(() => {
    if (!profile) return;
    setContactType(profile.contact_type);
    setPaymentTermId(profile.payment_term_id ?? "none");
    setCurrencyCode(profile.currency_code);
    setTaxNumber(profile.tax_number ?? "");
  }, [profile]);

  useEffect(() => {
    let cancelled = false;
    setDocsLoading(true);
    setDocsError(null);
    setDocsResolved(false);
    setInvoices([]);
    setBills([]);
    setDirectoryName(null);
    setCounterpartyLegalEntityId(null);
    setProfile(null);
    setProfileMissing(false);
    setProfileError(null);
    setError(null);
    setNotice(null);
    Promise.allSettled([listInvoices(), listBills(), listContacts()]).then(
      ([invoiceRead, billRead, contactRead]) => {
        if (cancelled) return;
        const failures: string[] = [];
        const fetchedInvoices =
          invoiceRead.status === "fulfilled" ? invoiceRead.value : [];
        if (invoiceRead.status === "rejected") {
          failures.push(`invoices: ${toErrorMessage(invoiceRead.reason)}`);
        }
        const fetchedBills =
          billRead.status === "fulfilled" ? billRead.value : [];
        if (billRead.status === "rejected") {
          failures.push(`bills: ${toErrorMessage(billRead.reason)}`);
        }
        if (contactRead.status === "fulfilled") {
          setDirectoryName(
            contactRead.value.find((contact) => contact.id === contactId)
              ?.display_name ?? null,
          );
        } else {
          failures.push(`contacts: ${toErrorMessage(contactRead.reason)}`);
        }
        const ownInvoices = fetchedInvoices.filter(
          (row) => row.crm_contact_id === contactId,
        );
        const ownBills = fetchedBills.filter(
          (row) => row.crm_contact_id === contactId,
        );
        setInvoices(ownInvoices);
        setBills(ownBills);
        setCounterpartyLegalEntityId(
          [...ownInvoices, ...ownBills].find(
            (row) => row.counterparty_legal_entity_id,
          )?.counterparty_legal_entity_id ?? null,
        );
        setDocsResolved(true);
        setDocsError(
          failures.length
            ? `Could not load ${failures.join("; ")}. Documents shown are only what loaded — retry to refresh them.`
            : null,
        );
        setDocsLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [contactId, docsReloadToken]);

  const createProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!counterpartyLegalEntityId) {
      setProfileError(
        "This contact has no counterparty legal entity yet. Link one of its invoices or bills to a legal entity first.",
      );
      return;
    }
    setSaving(true);
    setProfileError(null);
    setNotice(null);
    try {
      const selected = paymentTerms.find((term) => term.id === paymentTermId);
      if (selected && !selected.active) {
        throw new Error(
          "This saved payment term is inactive. Choose an active replacement or clear the profile default.",
        );
      }
      await upsertContactFinanceProfile({
        counterparty_legal_entity_id: counterpartyLegalEntityId,
        contact_type: contactType,
        tax_number: taxNumber.trim() ? taxNumber.trim() : undefined,
        payment_terms_days: 0,
        payment_term_id: paymentTermId === "none" ? null : paymentTermId,
        currency_code: currencyCode.trim().toUpperCase(),
      });
      await reloadProfile();
      setNotice(`Finance profile saved for ${contactId}.`);
    } catch (cause) {
      setProfileError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const statementSupplierName =
    bills[0]?.supplier_name ?? invoices[0]?.customer_name ?? "";

  const downloadStatement = async () => {
    if (!statementSupplierName) {
      setError(
        "No supplier name on record for this contact — nothing to print.",
      );
      return;
    }
    setPdfBusy(true);
    setError(null);
    try {
      const end = new Date().toISOString().slice(0, 10);
      await downloadSupplierStatementPdf({
        supplier_name: statementSupplierName,
        start_date: "2020-01-01",
        end_date: end,
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setPdfBusy(false);
    }
  };

  const openInvoices = invoices.filter((row) => isOpen(row.status));
  const openBills = bills.filter((row) => isOpen(row.status));
  const receivableCents = openInvoices.reduce(
    (sum, row) => sum + (row.amount_due_cents ?? row.total_minor),
    0,
  );
  const payableCents = openBills.reduce((sum, row) => sum + row.total_cents, 0);
  const displayName =
    directoryName ??
    invoices[0]?.customer_name ??
    bills[0]?.supplier_name ??
    null;

  const statementLines = [
    ...invoices.map((row) => ({
      key: `inv-${row.id}`,
      date: row.due_at,
      label: `Invoice ${row.invoice_number}`,
      detail: row.status,
      inCents: row.total_minor,
      outCents: 0,
      to: "/invoices/$invoiceId" as const,
      params: { invoiceId: row.id },
    })),
    ...bills.map((row) => ({
      key: `bill-${row.id}`,
      date: row.due_at,
      label: `Bill ${row.bill_number}`,
      detail: row.status,
      inCents: 0,
      outCents: row.total_cents,
      to: "/bills/$billId" as const,
      params: { billId: row.id },
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  const statementIn = statementLines.reduce((sum, row) => sum + row.inCents, 0);
  const statementOut = statementLines.reduce(
    (sum, row) => sum + row.outCents,
    0,
  );

  const deepLinkScope = {
    organization_id: scope?.organization_id,
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title={displayName ?? "Contact"}
        description={
          <>
            Bookkeeping for contact{" "}
            <code className="font-mono text-xs">{contactId}</code>. Name and
            contact details are shared with the CRM; this page shows billing
            terms and money.
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      {docsError ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {docsError}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDocsReloadToken((token) => token + 1)}
              disabled={docsLoading}
            >
              {docsLoading ? "Retrying…" : "Retry"}
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      <Tabs
        tabs={TABS.map((item) => ({ ...item }))}
        value={tab}
        onValueChange={setTab}
        label="Contact sections"
      >
        {tab === "bookkeeping" ? (
          <div className="grid gap-4">
            <div className="surface grid gap-2 rounded-2xl p-5 sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-[var(--muted-foreground)]">
                  Owed to you (open)
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {money(receivableCents)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-[var(--muted-foreground)]">
                  You owe (open)
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {money(payableCents)}
                </p>
              </div>
              <div>
                <p
                  className="text-xs font-medium text-[var(--muted-foreground)]"
                  title="Open receivables minus open payables"
                >
                  Net position
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {money(receivableCents - payableCents)}
                </p>
              </div>
            </div>

            {profileLoading ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                Loading billing terms…
              </p>
            ) : profile || profileMissing ? (
              <form
                onSubmit={createProfile}
                className="surface grid gap-4 rounded-2xl p-5"
                aria-label={profile ? "Edit finance profile" : "Create finance profile"}
              >
                {profileError ? (
                  <div
                    role="alert"
                    className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
                  >
                    {profileError}
                  </div>
                ) : null}
                <div>
                  <h2 className="text-sm font-semibold">
                    {profile ? "Billing terms" : "Create billing terms"}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                    {profile
                      ? "Set this contact’s default payment term for new invoices and bills."
                      : "No finance profile yet for this CRM contact — create one."}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="grid gap-1 text-xs font-medium">
                    Type
                    <select
                      className="select"
                      value={contactType}
                      onChange={(e) => setContactType(e.target.value)}
                      aria-label="Contact type"
                    >
                      {CONTACT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label
                    htmlFor="contact-payment-term-id"
                    className="grid gap-1 text-xs font-medium"
                  >
                    Default payment term
                    <PaymentTermPicker
                      id="contact-payment-term-id"
                      value={paymentTermId}
                      onChange={setPaymentTermId}
                      terms={paymentTerms}
                      includeInactiveId={profile?.payment_term_id}
                    />
                  </label>
                  <label
                    htmlFor="contact-currency"
                    className="grid gap-1 text-xs font-medium"
                  >
                    Currency
                    <Input
                      id="contact-currency"
                      value={currencyCode}
                      onChange={(e) =>
                        setCurrencyCode(e.target.value.toUpperCase())
                      }
                      required
                      placeholder="USD"
                      maxLength={3}
                      autoComplete="off"
                      spellCheck={false}
                      className="font-mono uppercase"
                    />
                  </label>
                  <label
                    htmlFor="contact-tax-number"
                    className="grid gap-1 text-xs font-medium"
                  >
                    Tax number <span className="font-normal">(optional)</span>
                    <Input
                      id="contact-tax-number"
                      value={taxNumber}
                      onChange={(e) => setTaxNumber(e.target.value)}
                      placeholder="EU826…"
                      autoComplete="off"
                      spellCheck={false}
                      className="font-mono"
                    />
                  </label>
                </div>
                {paymentTermsError ? (
                  <p className="text-xs text-[var(--danger)]">
                    Could not load payment terms: {paymentTermsError}
                  </p>
                ) : null}
                <div>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Saving…" : profile ? "Save profile" : "Create profile"}
                  </Button>
                </div>
              </form>
            ) : profileError ? (
              <div
                role="alert"
                className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
              >
                {profileError}
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void reloadProfile()}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : docsError && !counterpartyLegalEntityId ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                Billing terms become available once this contact&apos;s
                invoices or bills load — retry the document read above.
              </p>
            ) : !counterpartyLegalEntityId ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                No billing terms yet. Create an invoice or bill for this
                contact to set its counterparty legal entity and default terms.
              </p>
            ) : null}

            <section aria-label="Open documents" className="grid gap-4">
              <h2 className="text-lg font-semibold tracking-tight">
                Open documents
              </h2>
              {docsLoading ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Loading…
                </p>
              ) : openInvoices.length === 0 && openBills.length === 0 ? (
                docsError ? (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    Open documents could not be loaded — retry above.
                  </p>
                ) : (
                  <p className="text-sm text-[var(--muted-foreground)]">
                    Nothing open — no unpaid invoices or bills reference this
                    contact.
                  </p>
                )
              ) : (
                <div className="surface overflow-hidden rounded-2xl">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {openInvoices.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link
                              to="/invoices/$invoiceId"
                              params={{ invoiceId: row.id }}
                              className="font-medium text-[var(--action)] underline-offset-4 hover:underline"
                            >
                              Invoice {row.invoice_number}
                            </Link>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.status}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(row.amount_due_cents ?? row.total_minor)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {openBills.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link
                              to="/bills/$billId"
                              params={{ billId: row.id }}
                              className="font-medium text-[var(--action)] underline-offset-4 hover:underline"
                            >
                              Bill {row.bill_number}
                            </Link>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.status}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(row.total_cents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="flex flex-wrap items-start gap-3">
              <div>
                <p className="text-sm text-[var(--muted-foreground)]">
                  Every invoice and bill for this contact, oldest first.
                </p>
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  The PDF matches the recorded supplier name exactly
                  {statementSupplierName ? ` (“${statementSupplierName}”)` : ""}{" "}
                  — the statement endpoint finds nothing on a near-miss name.
                </p>
              </div>
              <span className="ms-auto">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void downloadStatement()}
                  disabled={pdfBusy || statementLines.length === 0}
                  title="Supplier statement PDF for the recorded supplier name"
                >
                  {pdfBusy ? "Preparing…" : "Download statement (PDF)"}
                </Button>
              </span>
            </div>
            <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead className="text-right">Money in</TableHead>
                      <TableHead className="text-right">Money out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4}>Loading…</TableCell>
                      </TableRow>
                    ) : statementLines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          {docsError
                            ? "Documents could not be loaded — retry above."
                            : "No invoices or bills reference this contact yet."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      statementLines.map((line) => (
                        <TableRow key={line.key}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {String(line.date).slice(0, 10)}
                          </TableCell>
                          <TableCell className="text-sm">
                            <Link
                              to={line.to}
                              params={line.params}
                              className="font-medium text-[var(--action)] underline-offset-4 hover:underline"
                            >
                              {line.label}
                            </Link>{" "}
                            <span className="text-xs text-[var(--muted-foreground)]">
                              {line.detail}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.inCents > 0 ? money(line.inCents) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.outCents > 0 ? money(line.outCents) : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {statementLines.length > 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="font-semibold">
                          Total
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {money(statementIn)}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {money(statementOut)}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="statement-stack"
            >
              {docsLoading ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Loading…
                </p>
              ) : statementLines.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  {docsError
                    ? "Documents could not be loaded — retry above."
                    : "No invoices or bills reference this contact yet."}
                </p>
              ) : (
                statementLines.map((line) => (
                  <article
                    key={line.key}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to={line.to}
                        params={line.params}
                        className="record-title underline-offset-4 hover:underline"
                      >
                        {line.label}
                      </Link>
                      <span className="tabular-nums">
                        {line.inCents > 0
                          ? `+${money(line.inCents)}`
                          : `−${money(line.outCents)}`}
                      </span>
                    </div>
                    <CardField label="Date">
                      {String(line.date).slice(0, 10)}
                    </CardField>
                    <CardField label="Status">{line.detail}</CardField>
                    <CardField label="Money in">
                      {line.inCents > 0 ? money(line.inCents) : "—"}
                    </CardField>
                    <CardField label="Money out">
                      {line.outCents > 0 ? money(line.outCents) : "—"}
                    </CardField>
                  </article>
                ))
              )}
            </div>
          </div>
        )}
      </Tabs>

      <section aria-label="Outside accounting" className="grid gap-4">
        <h2 className="text-lg font-semibold tracking-tight">
          Outside accounting
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <DeepLinkCard
            target="crm"
            path={`/contacts/${contactId}`}
            scope={deepLinkScope}
            title="People and activity"
            body="Contact details, people, and the full activity history live in the CRM."
          />
          <DeepLinkCard
            target="documents"
            scope={deepLinkScope}
            title="Files and receipts"
            body="Uploaded receipts and source files for this contact live in the document store."
          />
        </div>
      </section>
    </div>
  );
}
